/**
 * Utility functions for safe JSON/JSONB parsing
 * Handles edge cases like double-encoded strings, null values, and parse errors
 *
 * Updated Mar 2026: Added parseConditionsFromDb for the new multi-condition system.
 */

import type { FieldCondition } from '../types/needs';

/**
 * Safely parses field options from various formats into a string array
 * Handles: arrays (pass through), JSON strings (parse), null/undefined (empty array)
 *
 * @param options - The options value from database (may be array, string, or null)
 * @returns Always returns a string array, never throws
 */
export function safeParseOptions(options: unknown): string[] {
  // Already an array - return as-is
  if (Array.isArray(options)) {
    return options;
  }

  // Null or undefined - return empty array
  if (options === null || options === undefined) {
    return [];
  }

  // String - try to parse as JSON
  if (typeof options === 'string') {
    try {
      const parsed = JSON.parse(options);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      // Parse failed - return empty array
      return [];
    }
  }

  // Unknown type - return empty array
  return [];
}

/**
 * Parses conditions from the database, with fallback to legacy columns.
 * Prefers the new `conditions` JSONB array when populated;
 * falls back to single-condition legacy format (condition_source_field_id + condition_values).
 *
 * @param conditions - Value of the new `conditions` jsonb column
 * @param legacySourceId - Legacy condition_source_field_id column value
 * @param legacyValues - Legacy condition_values column value
 * @returns Array of FieldCondition objects, or null if no conditions
 */
export function parseConditionsFromDb(
  conditions: unknown,
  legacySourceId: string | null | undefined,
  legacyValues: unknown
): FieldCondition[] | null {
  if (Array.isArray(conditions) && conditions.length > 0) {
    return conditions as FieldCondition[];
  }
  if (legacySourceId) {
    const values = Array.isArray(legacyValues) ? (legacyValues as string[]) : [];
    return [{ source_field_id: legacySourceId, values }];
  }
  return null;
}
